import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { apiRequest } from '@/utils/api-request';
import { Camera, Trash2, Star, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Upload, X, Play, Loader2 } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import imageCompression from 'browser-image-compression';

interface StoryData {
    id: number;
    media_url: string;
    description: string | null;
    is_highlighted: number;
    like_count: number;
    media_type: string | null;
    source_type: string;
    created_at: string;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const CampusStories: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [stories, setStories] = useState<StoryData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [description, setDescription] = useState('');
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedStory, setSelectedStory] = useState<StoryData | null>(null);

    // Delete confirmation
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [storyToDelete, setStoryToDelete] = useState<StoryData | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Fetch stories
    const fetchStories = async (page: number = pagination.currentPage) => {
        try {
            setIsLoading(true);
            const response = await apiRequest(`/stories/${id}?page=${page}&limit=${pagination.itemsPerPage}`);

            if (response.success) {
                setStories(response.data.stories || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch stories', 'error');
            }
        } catch (error) {
            console.error('Error fetching stories:', error);
            showToast('Failed to fetch stories', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Load stories on mount
    useEffect(() => {
        fetchStories(1);
    }, [id]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchStories(newPage);
        }
    };

    const [isCompressing, setIsCompressing] = useState(false);

    // Handle file selection
    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const maxSizeBytes = 5 * 1024 * 1024; // 5MB
        const isImage = file.type.startsWith('image/');
        const isVideoFile = file.type.startsWith('video/');

        // For videos, we can't compress - must reject if too large
        if (isVideoFile && file.size > maxSizeBytes) {
            showToast('Video file size must be less than 5MB. Please compress your video using an external tool.', 'error');
            return;
        }

        let fileToUse = file;

        // For images, compress if needed
        if (isImage && file.size > maxSizeBytes) {
            try {
                setIsCompressing(true);
                showToast(`Compressing image from ${(file.size / 1024 / 1024).toFixed(2)}MB...`, 'info');

                const options = {
                    maxSizeMB: 5,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    fileType: file.type as string,
                };

                fileToUse = await imageCompression(file, options);

                const originalSizeKB = (file.size / 1024).toFixed(1);
                const compressedSizeKB = (fileToUse.size / 1024).toFixed(1);
                showToast(`Image compressed: ${originalSizeKB}KB → ${compressedSizeKB}KB`, 'success');
            } catch (err) {
                console.error('Error compressing image:', err);
                showToast('Failed to compress image. Please try a smaller file.', 'error');
                setIsCompressing(false);
                return;
            } finally {
                setIsCompressing(false);
            }
        }

        setSelectedFile(fileToUse);

        // Create preview URL
        const url = URL.createObjectURL(fileToUse);
        setPreviewUrl(url);
    };

    // Handle description change with word count
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;

        // Check for URLs
        const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
        if (urlPattern.test(text)) {
            showToast('URLs are not allowed in description', 'error');
            return;
        }

        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        if (words.length <= 30) {
            setDescription(text);
            setWordCount(words.length);
        }
    };

    // Reset form
    const resetForm = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setDescription('');
        setIsHighlighted(false);
        setWordCount(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    // Handle submit
    const handleSubmit = async () => {
        if (!selectedFile) {
            showToast('Please select a media file', 'error');
            return;
        }

        try {
            setIsSubmitting(true);

            const formData = new FormData();
            formData.append('file', selectedFile);
            if (description) {
                formData.append('description', description);
            }
            formData.append('is_highlighted', isHighlighted.toString());

            const token = Cookies.get('crm_auth_token');
            const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/stories/${id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                credentials: 'include',
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                showToast('Story created successfully', 'success');
                handleCloseModal();
                fetchStories(1);
            } else {
                showToast(data.message || 'Failed to create story', 'error');
            }
        } catch (error) {
            console.error('Error creating story:', error);
            showToast('Failed to create story', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle view story
    const handleViewStory = (story: StoryData) => {
        setSelectedStory(story);
        setViewModalOpen(true);
    };

    // Handle delete click
    const handleDeleteClick = (story: StoryData, e: React.MouseEvent) => {
        e.stopPropagation();
        setStoryToDelete(story);
        setDeleteConfirmOpen(true);
    };

    // Handle delete confirm
    const handleDeleteConfirm = async () => {
        if (!storyToDelete) return;

        try {
            setIsDeleting(true);
            const response = await apiRequest(`/stories/${storyToDelete.id}`, {
                method: 'DELETE',
            });

            if (response.success) {
                showToast('Story deleted successfully', 'success');
                setDeleteConfirmOpen(false);
                setStoryToDelete(null);
                fetchStories(pagination.currentPage);
            } else {
                showToast('Failed to delete story', 'error');
            }
        } catch (error) {
            console.error('Error deleting story:', error);
            showToast('Failed to delete story', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/am/i, 'AM').replace(/pm/i, 'PM');
        return `${dateStr}, ${timeStr}`;
    };

    // Check if media is video
    const isVideo = (url: string, mediaType: string | null) => {
        if (mediaType === 'VIDEO') return true;
        return url?.match(/\.(mp4|webm|mov)$/i);
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Camera className="h-5 w-5" />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Campus Stories</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {pagination.totalItems}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Share stories from your campus</p>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => setIsModalOpen(true)}
                        leftIcon={<Upload size={16} />}
                    >
                        Add Story
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <p className="text-gray-500">Loading stories...</p>
                    </div>
                ) : stories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                        <Camera size={48} className="mb-4 opacity-50" />
                        <p>No stories yet</p>
                        <p className="text-sm">Click "Add Story" to share your first campus story</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {stories.map((story) => (
                            <div
                                key={story.id}
                                className="relative group cursor-pointer rounded-lg overflow-hidden bg-white shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                                onClick={() => handleViewStory(story)}
                            >
                                {/* Media Preview */}
                                <div className="aspect-[9/16] bg-gray-100 relative">
                                    {isVideo(story.media_url, story.media_type) ? (
                                        <>
                                            <video
                                                src={story.media_url}
                                                className="w-full h-full object-cover"
                                                muted
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                <Play size={32} className="text-white" />
                                            </div>
                                        </>
                                    ) : (
                                        <img
                                            src={story.media_url}
                                            alt="Story"
                                            className="w-full h-full object-cover"
                                        />
                                    )}

                                    {/* Highlighted badge */}
                                    {story.is_highlighted === 1 && (
                                        <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
                                            <Star size={10} fill="white" />
                                            Highlighted
                                        </div>
                                    )}

                                    {/* Delete button */}
                                    <button
                                        onClick={(e) => handleDeleteClick(story, e)}
                                        className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>

                                {/* Info */}
                                <div className="p-3">
                                    <p className="text-xs text-gray-500">{formatDate(story.created_at)}</p>
                                    {story.description && (
                                        <p className="text-sm text-gray-700 mt-1 line-clamp-2">{story.description}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 border-t bg-white">
                    <div className="text-sm text-gray-600">
                        Showing {pagination.totalItems === 0 ? 0 : ((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                        {pagination.totalItems} stories
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                        >
                            <ChevronsLeft size={16} />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                        >
                            <ChevronLeft size={16} />
                        </Button>
                        <span className="text-sm text-gray-600">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                        >
                            <ChevronRight size={16} />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                        >
                            <ChevronsRight size={16} />
                        </Button>
                    </div>
                </div>
            )}

            {/* Add Story Modal */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Add Campus Story"
                subtitle="Share a story from your campus"
                icon={<div className="text-brandColor"><Camera size={24} /></div>}
                width="500px"
            >
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* File Upload */}
                    <div>
                        <label className="text-sm font-semibold text-gray-900 mb-2 block">
                            Upload Media <span className="text-red-500">*</span>
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,video/mp4,video/webm"
                            onChange={handleFileSelect}
                            className="hidden"
                        />

                        {!previewUrl ? (
                            <div
                                onClick={() => !isCompressing && fileInputRef.current?.click()}
                                className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-brandColor transition-colors ${isCompressing ? 'opacity-50 cursor-wait' : ''}`}
                            >
                                {isCompressing ? (
                                    <>
                                        <Loader2 size={32} className="mx-auto text-brandColor mb-2 animate-spin" />
                                        <p className="text-sm text-brandColor font-semibold">Compressing image...</p>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-600">Click to upload image or video</p>
                                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF, WebP, MP4, WebM (max 5MB)</p>
                                        <p className="text-xs text-green-600 mt-1">✓ Large images will be automatically compressed</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="aspect-[9/16] max-h-[300px] bg-gray-100 rounded-lg overflow-hidden">
                                    {selectedFile?.type.startsWith('video/') ? (
                                        <video
                                            src={previewUrl}
                                            className="w-full h-full object-contain"
                                            controls
                                        />
                                    ) : (
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            className="w-full h-full object-contain"
                                        />
                                    )}
                                </div>
                                <button
                                    onClick={resetForm}
                                    className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-semibold text-gray-900">
                                Description <span className="text-gray-400">(Optional)</span>
                            </label>
                            <span className={`text-xs ${wordCount >= 25 ? 'text-orange-500' : 'text-gray-400'}`}>
                                {wordCount}/30 words
                            </span>
                        </div>
                        <textarea
                            value={description}
                            onChange={handleDescriptionChange}
                            placeholder="Add a brief description (no URLs allowed)"
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Highlighted */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="highlighted"
                            checked={isHighlighted}
                            onChange={(e) => setIsHighlighted(e.target.checked)}
                            className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
                        />
                        <label htmlFor="highlighted" className="text-sm text-gray-700 flex items-center gap-2">
                            <Star size={16} className="text-yellow-500" />
                            Mark as Highlighted Story
                        </label>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                    <div className="flex gap-3 justify-end">
                        <Button
                            variant="outline"
                            onClick={handleCloseModal}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !selectedFile}
                        >
                            {isSubmitting ? 'Uploading...' : 'Upload Story'}
                        </Button>
                    </div>
                </div>
            </SettingsSlideModal>

            {/* View Story Modal */}
            <SettingsSlideModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                title="Story Details"
                subtitle={selectedStory ? formatDate(selectedStory.created_at) : ''}
                icon={<div className="text-brandColor"><Camera size={24} /></div>}
                width="500px"
            >
                {selectedStory && (
                    <>
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {/* Media */}
                            <div className="aspect-[9/16] max-h-[400px] bg-gray-100 rounded-lg overflow-hidden">
                                {isVideo(selectedStory.media_url, selectedStory.media_type) ? (
                                    <video
                                        src={selectedStory.media_url}
                                        className="w-full h-full object-contain"
                                        controls
                                    />
                                ) : (
                                    <img
                                        src={selectedStory.media_url}
                                        alt="Story"
                                        className="w-full h-full object-contain"
                                    />
                                )}
                            </div>

                            {/* Info */}
                            <div className="space-y-3">
                                {selectedStory.is_highlighted === 1 && (
                                    <div className="flex items-center gap-2 text-yellow-600">
                                        <Star size={16} fill="currentColor" />
                                        <span className="text-sm font-medium">Highlighted Story</span>
                                    </div>
                                )}

                                {selectedStory.description && (
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Description</label>
                                        <p className="text-sm text-gray-800 mt-1">{selectedStory.description}</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Likes</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedStory.like_count || 0}</p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Source</label>
                                        <p className="text-sm font-medium text-gray-900 capitalize">{selectedStory.source_type}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div className="flex gap-3 justify-between">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setViewModalOpen(false);
                                        setStoryToDelete(selectedStory);
                                        setDeleteConfirmOpen(true);
                                    }}
                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                    leftIcon={<Trash2 size={16} />}
                                >
                                    Delete
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setViewModalOpen(false)}
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </SettingsSlideModal>

            {/* Delete Confirmation Modal */}
            {deleteConfirmOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Story</h3>
                        <p className="text-gray-600 mb-6">Are you sure you want to delete this story? This action cannot be undone.</p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setDeleteConfirmOpen(false);
                                    setStoryToDelete(null);
                                }}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampusStories;
